import dynamic from "next/dynamic";
import { GetResourcesHomepageQuery } from "gql/types/graphql";
import { Meta } from "components/atoms/Meta";
import { LatestCaseStudy, getPageTranslations } from "utils/server";
import { strapiService } from "@services";
import { YuLifeLocale } from "@hooks";
import { Logger } from "services/logger";
import { GENERIC_CATEGORIES_ERROR, GENERIC_RESOURCES_ERROR } from "utils/_constants";
import Custom404 from "pages/404";
import { getStrapiLocale } from "utils/locales/strapi";
import { useMetadata } from "hooks/useMetadata";
import { StaticRoutes } from "@routes";
import { isLocaleInStaticRoute } from "@utils";

const ResourcesComponent = dynamic(() => import("../../components/pages/Resources"));

export interface Category {
  order?: string;
  name?: string;
  slug?: string;
  locale?: string;
  description?: string;
  iconSrc?: string;
  iconAlt?: string;
}

export default function Resources({
  featuredResources,
  blogs,
  caseStudies,
  newsEvents,
  categories,
  locale,
  latestCaseStudy,
}: {
  featuredResources: GetResourcesHomepageQuery["getResourcesHomepage"]["featuredResources"];
  blogs: GetResourcesHomepageQuery["getResourcesHomepage"]["blogs"];
  caseStudies: GetResourcesHomepageQuery["getResourcesHomepage"]["caseStudies"];
  newsEvents: GetResourcesHomepageQuery["getResourcesHomepage"]["newsEvents"];
  latestCaseStudy: LatestCaseStudy;
  categories: Category[];
  locale: YuLifeLocale;
}) {
  const metadata = useMetadata("resource-hub");

  return (
    <>
      <Meta locale={locale} {...metadata} />
      <ResourcesComponent
        blogs={blogs}
        caseStudies={caseStudies}
        newsEvents={newsEvents}
        featuredResources={featuredResources}
        latestCaseStudy={latestCaseStudy}
        categories={categories}
      />
    </>
  );
}

export const getStaticProps = async ({ locale }) => {
  if (!isLocaleInStaticRoute(locale, StaticRoutes.resourceHub)) {
    return {
      notFound: true,
    };
  }

  const strapiLocale = getStrapiLocale(locale);
  const resources = await strapiService.getResourceHomePage(strapiLocale);
  const categories = await strapiService.getBlogTags(strapiLocale);

  if (!isLocaleInStaticRoute(locale, StaticRoutes.resourceHub)) {
    return {
      notFound: true,
    };
  }

  if (!resources) {
    Logger.error(GENERIC_RESOURCES_ERROR);
    return { notFound: true };
  }

  if (!categories.length) {
    Logger.error(GENERIC_CATEGORIES_ERROR);
    return { notFound: true };
  }

  const genericProps = await strapiService.getGenericProps(locale, {
    hasKeyResources: false,
    hasLatestCaseStudy: true,
  });

  return {
    props: {
      featuredResources: resources?.featuredResources || [],
      newsEvents: resources?.newsEvents || [],
      blogs: resources?.blogs || [],
      caseStudies: resources?.caseStudies || [],
      categories: categories || [],
      messages: await getPageTranslations(locale),
      ...genericProps,
    },
    revalidate: 300,
  };
};
